import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Gift, Award, LogOut, Calendar, Ticket, Check, TrendingUp, PiggyBank, Users, Copy } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { useAuth } from "../context/AuthContext";
import { useBooking } from "../context/BookingContext";
import { ShopTab } from "../components/member/ShopTab";
import { OrdersTab } from "../components/member/OrdersTab";
import { AppointmentsTab } from "../components/member/AppointmentsTab";
import { WalletTab } from "../components/member/WalletTab";
import { ProfileTab } from "../components/member/ProfileTab";
import { SavedTab } from "../components/member/SavedTab";
import { CheckInTab } from "../components/member/CheckInTab";
import { MyReferralsCard } from "../components/member/MyReferralsCard";
import { ReviewCard, ReviewModal } from "../components/ReviewRewards";
import api, { formatApiError } from "../lib/api";

export default function Dashboard() {
  const { user, logout, refreshUser } = useAuth();
  const { openBooking } = useBooking();
  const navigate = useNavigate();
  const [rewards, setRewards] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [reviews, setReviews] = useState(null);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [cardDismissed, setCardDismissed] = useState(() => localStorage.getItem("review_card_dismissed") === "1");
  const [redeeming, setRedeeming] = useState(null);

  const load = () => {
    api.get("/rewards").then((r) => setRewards(r.data)).catch(() => {});
    api.get("/membership-tiers").then((r) => setTiers(r.data)).catch(() => {});
    api.get("/member/rewards/redemptions").then((r) => setRedemptions(r.data)).catch(() => {});
    api.get("/member/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/config").then((r) => setReviews(r.data.reviews)).catch(() => {});
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "overview";
  const setTab = (t) => setSearchParams(t === "overview" ? {} : { tab: t });

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) return;
    const kind = searchParams.get("kind");
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (kind === "membership") {
            toast.success("Membership activated — welcome to the club!");
            await refreshUser();
            setSearchParams({});
          } else {
            toast.success("Payment successful — thank you for your order!");
            await refreshUser();
            setSearchParams({ tab: "orders" });
          }
          return;
        }
        if (data.status === "expired") {
          toast.error("Payment session expired.");
          setSearchParams(kind === "membership" ? {} : { tab: "shop" });
          return;
        }
      } catch {}
      if (attempts++ < 6) setTimeout(poll, 2000);
    };
    toast.loading("Confirming your payment…", { id: "pay", duration: 3000 });
    poll();
    // eslint-disable-next-line
  }, []);

  // Show the review-reward prompt once per session until both are claimed
  useEffect(() => {
    if (!user) return;
    const c = user.review_claims || {};
    if (c.google && c.yelp) return;
    if (sessionStorage.getItem("review_modal_seen") === "1") return;
    sessionStorage.setItem("review_modal_seen", "1");
    setReviewModalOpen(true);
  }, [user]);

  if (!user) return null;

  const currentTier = tiers.find((t) => t.name === user.tier);
  const totalSavings = orders.reduce((s, o) => s + (o.savings || 0), 0);
  const claims = user.review_claims || {};
  const bothClaimed = claims.google && claims.yelp;

  const redeem = async (reward) => {
    setRedeeming(reward.id);
    try {
      const { data } = await api.post("/member/rewards/redeem", { reward_id: reward.id });
      toast.success(data.message, { description: `Code: ${data.redemption.code}` });
      await refreshUser();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not redeem");
    } finally {
      setRedeeming(null);
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="pt-28 md:pt-32 pb-24 bg-stone min-h-screen" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#8A9492]">Member portal</p>
            <h1 className="font-heading text-3xl md:text-4xl font-light text-ink mt-1">
              Hello, {user.name?.split(" ")[0]}
            </h1>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => openBooking()} data-testid="dashboard-book-button"
              className="iri-gradient text-ink rounded-full font-medium hover:scale-105 transition-transform">
              <Calendar className="h-4 w-4 mr-2" /> Book appointment
            </Button>
            <Button onClick={doLogout} variant="outline" data-testid="logout-button" className="rounded-full border-ink/20 text-ink">
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </Button>
          </div>
        </div>

        {/* tab nav */}
        <div className="flex flex-wrap gap-2 mb-8 border-b border-slate-200 pb-2" data-testid="dashboard-tabs">
          {[["overview", "Overview"], ["appointments", "Appointments"], ["wallet", "Wallet"], ["checkin", "Check-In"], ["shop", "Shop"], ["orders", "Orders"], ["saved", "Saved"], ["profile", "Profile"]].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`tab-${key}`}
              className={`px-4 py-2 rounded-full text-sm transition-all ${tab === key ? "iri-gradient text-ink font-medium" : "text-[#5C6664] hover:text-ink"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "overview" && (<>
        {!bothClaimed && !cardDismissed && (
          <ReviewCard
            reviews={reviews}
            claims={claims}
            onClaimed={refreshUser}
            onDismiss={() => { setCardDismissed(true); localStorage.setItem("review_card_dismissed", "1"); }}
          />
        )}
        {/* stat cards */}
        <div className="grid md:grid-cols-3 gap-5 mb-6">
          <div className="iri-gradient rounded-2xl p-7" data-testid="points-card">
            <TrendingUp className="h-6 w-6 text-ink/70" strokeWidth={1.5} />
            <p className="text-ink/70 text-sm mt-4">Reward points</p>
            <p className="font-heading text-5xl font-light text-ink mt-1" data-testid="points-value">{user.points}</p>
          </div>
          <div className="bg-white rounded-2xl p-7 border border-slate-100" data-testid="tier-card">
            <Award className="h-6 w-6 text-iri-mid" strokeWidth={1.5} />
            <p className="text-[#8A9492] text-sm mt-4">Membership tier</p>
            <p className="font-heading text-3xl font-light text-ink mt-1" data-testid="tier-value">
              {user.tier && user.tier !== "none" ? user.tier : "No active tier"}
            </p>
            {(!user.tier || user.tier === "none") && (
              <button onClick={() => navigate("/membership")} className="text-sm text-iri-mid underline mt-2" data-testid="join-membership-link">
                Explore memberships →
              </button>
            )}
          </div>
          <div className="bg-white rounded-2xl p-7 border border-slate-100" data-testid="redemptions-count-card">
            <Ticket className="h-6 w-6 text-iri-mid" strokeWidth={1.5} />
            <p className="text-[#8A9492] text-sm mt-4">Rewards redeemed</p>
            <p className="font-heading text-5xl font-light text-ink mt-1">{redemptions.length}</p>
          </div>
          <div className="bg-white rounded-2xl p-7 border border-slate-100" data-testid="member-savings-card">
            <PiggyBank className="h-6 w-6 text-iri-mid" strokeWidth={1.5} />
            <p className="text-[#8A9492] text-sm mt-4">Member savings</p>
            <p className="font-heading text-5xl font-light text-ink mt-1" data-testid="member-savings-value">
              ${totalSavings.toFixed(0)}
            </p>
            <p className="text-xs text-[#8A9492] mt-1">Saved on shop orders</p>
          </div>
        </div>

        {/* points progress to next reward */}
        {rewards.length > 0 && (() => {
          const sorted = [...rewards].sort((a, b) => a.cost - b.cost);
          const next = sorted.find((r) => r.cost > user.points);
          const affordable = sorted.filter((r) => r.cost <= user.points).length;
          const pct = next ? Math.min(100, Math.round((user.points / next.cost) * 100)) : 100;
          return (
            <div className="bg-white rounded-2xl p-7 border border-slate-100 mb-6" data-testid="points-progress-card">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-iri-mid" strokeWidth={1.5} />
                  <h2 className="font-heading text-xl font-medium text-ink">
                    {next ? `${next.cost - user.points} points to your next reward` : "You've unlocked every reward!"}
                  </h2>
                </div>
                {affordable > 0 && (
                  <span className="text-xs iri-gradient text-ink font-medium px-3 py-1 rounded-full" data-testid="rewards-ready-badge">
                    {affordable} reward{affordable > 1 ? "s" : ""} ready to redeem
                  </span>
                )}
              </div>
              <div className="mt-4 h-3 bg-stone rounded-full overflow-hidden">
                <div className="h-full iri-gradient transition-all duration-700" style={{ width: `${pct}%` }} data-testid="points-progress-bar" />
              </div>
              <div className="flex items-center justify-between mt-2 text-xs text-[#8A9492]">
                <span>{user.points} pts</span>
                {next ? <span>{next.name} · {next.cost} pts</span> : <span>Max tier reached</span>}
              </div>
            </div>
          );
        })()}

        {/* refer a friend */}
        {user.referral_code && (
          <div className="rounded-2xl p-7 mb-6 bg-ink text-white relative overflow-hidden" data-testid="referral-card">
            <Users className="h-6 w-6 text-iri-start" strokeWidth={1.5} />
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mt-4">
              <div className="max-w-lg">
                <h2 className="font-heading text-xl font-medium">Refer a friend, earn rewards</h2>
                <p className="text-white/60 text-sm mt-1.5">
                  Share your code. When a friend signs up and completes their first paid visit,
                  you get <strong className="text-white">500 points</strong> and they get <strong className="text-white">150 points</strong>.
                </p>
                {user.referral_count > 0 && (
                  <p className="text-iri-start text-sm mt-2" data-testid="referral-count">
                    {user.referral_count} friend{user.referral_count > 1 ? "s" : ""} referred so far 🎉
                  </p>
                )}
              </div>
              <div className="shrink-0">
                <p className="text-xs uppercase tracking-wider text-white/50 mb-1.5">Your code</p>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-lg font-medium bg-white/10 rounded-lg px-4 py-2.5" data-testid="referral-code">{user.referral_code}</span>
                  <Button
                    onClick={() => {
                      const link = `${window.location.origin}/register?ref=${user.referral_code}`;
                      navigator.clipboard?.writeText(link);
                      toast.success("Referral link copied!");
                    }}
                    data-testid="referral-copy"
                    className="iri-gradient text-ink rounded-lg h-11 gap-2"
                  >
                    <Copy className="h-4 w-4" /> Copy link
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* my referrals */}
        {user.referral_code && <MyReferralsCard />}

        {/* tier benefits */}
        {currentTier && (          <div className="bg-white rounded-2xl p-7 border border-slate-100 mb-6" data-testid="tier-benefits">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="h-5 w-5 text-iri-mid" />
              <h2 className="font-heading text-xl font-medium text-ink">Your {currentTier.name} benefits</h2>
            </div>
            <ul className="grid sm:grid-cols-2 gap-3">
              {currentTier.benefits.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm text-[#5C6664]">
                  <Check className="h-4 w-4 mt-0.5 text-iri-mid shrink-0" /> {b}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* rewards catalog */}
        <div className="mb-6">
          <h2 className="font-heading text-2xl font-light text-ink mb-4">Redeem your points</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {rewards.map((r) => {
              const affordable = user.points >= r.cost;
              return (
                <div key={r.id} className="bg-white rounded-2xl p-6 border border-slate-100 flex flex-col" data-testid={`redeem-card-${r.id}`}>
                  <div className="flex items-center justify-between">
                    <Gift className="h-5 w-5 text-iri-mid" strokeWidth={1.5} />
                    <span className="iri-gradient text-ink text-sm font-medium px-3 py-1 rounded-full">{r.cost} pts</span>
                  </div>
                  <h3 className="font-heading text-lg font-medium text-ink mt-4">{r.name}</h3>
                  <p className="text-sm text-[#5C6664] mt-1 flex-1">{r.description}</p>

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        disabled={!affordable || redeeming === r.id}
                        data-testid={`redeem-button-${r.id}`}
                        className={`mt-5 rounded-full w-full ${affordable ? "bg-ink text-white hover:bg-ink/90" : "bg-slate-100 text-[#8A9492]"}`}
                      >
                        {affordable ? (redeeming === r.id ? "Redeeming…" : "Redeem") : `Need ${r.cost - user.points} more pts`}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid="redeem-confirm-dialog">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Redeem {r.name}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will deduct {r.cost} points from your balance and generate a redemption code you can use on your next visit.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid="redeem-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => redeem(r)} data-testid="redeem-confirm" className="iri-gradient text-ink">
                          Confirm redemption
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              );
            })}
          </div>
        </div>

        {/* redemption history */}
        <div className="bg-white rounded-2xl p-7 border border-slate-100" data-testid="redemption-history">
          <h2 className="font-heading text-xl font-medium text-ink mb-4">Redemption history</h2>
          {redemptions.length === 0 ? (
            <p className="text-sm text-[#8A9492]">No redemptions yet. Start redeeming your points above!</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {redemptions.slice().reverse().map((r) => (
                <li key={r.id} className="py-4 flex items-center justify-between" data-testid={`history-item-${r.id}`}>
                  <div>
                    <p className="text-ink font-medium">{r.reward_name}</p>
                    <p className="text-xs text-[#8A9492] mt-0.5">
                      Code <span className="font-mono text-ink">{r.code}</span> · {new Date(r.redeemed_at).toLocaleDateString()}
                    </p>
                  </div>
                  <span className="text-sm text-[#5C6664]">−{r.cost} pts</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        </>)}

        {tab === "checkin" && <CheckInTab user={user} />}
        {tab === "appointments" && <AppointmentsTab />}
        {tab === "wallet" && <WalletTab />}
        {tab === "shop" && <ShopTab user={user} />}
        {tab === "orders" && <OrdersTab />}
        {tab === "saved" && <SavedTab onChange={refreshUser} />}
        {tab === "profile" && <ProfileTab user={user} onSaved={refreshUser} />}
      </div>
      <ReviewModal
        open={reviewModalOpen}
        onOpenChange={setReviewModalOpen}
        reviews={reviews}
        claims={claims}
        onClaimed={refreshUser}
      />
    </div>
  );
}
